import cloneDeep from "lodash/cloneDeep";

import {filter_routes} from "../../routes";
import {eventBus} from "../../util/eventbus";
import {createUUIDv4, getWindowDimensions} from "../../util/helpers";
import AjaxUtils from "../../util/ajax_utils";
import {ClearToken} from "../../util/jwt";

const ApckAppLink = {
    props: {
        route: {
            type: Object,
            default: () => ({})
        },
    },
    computed: {
        isExternal() {
            return this.route.meta?.isExternal || false;
        },
        newTab() {
            return this.route.meta?.newTab || false;
        },
    },
    template: `
        <a v-if="isExternal" :href="route.path" :target="newTab ? '_blank': '_self'">
            <slot />
        </a>
        <div v-else-if="route.handler" @click="route.handler" @keyup.enter="route.handler">
            <slot />
        </div>
        <router-link v-else :to="{name: route.name}">
            <slot />
        </router-link>
    `
}

const ApckNavItem = {
    name: 'apck-nav-item',
    components: {
        'apck-app-link': ApckAppLink,
        'apck-nav-item': this
    },
    props: {
        route: null,
        parent_active: {
            type: String,
            default: null,
        },
        use_div: {
            type: Boolean,
            default: false,
        },
        category_link_as_child: {
            type: Boolean,
            default: false,
        },
        icon: {
            type: Boolean,
            default: true,
        },
        child_icons: {
            type: Boolean,
            default: false,
        },
        nav_parent: {
            type: Object,
            default: () => ({})
        }
    },
    data () {
        return {
            expanded: false,
            uuid: "",
            _preventCloseOnClickOutside: false,  // set to true during an event to prevent closing the tooltip
        }
    },
    created() {
        this.uuid = createUUIDv4();
    },
    mounted() {
        if (this.active) {
            if (!this.nav_parent.$data.sidebarCollapsed) {
                // prevent closing category after navigating to new route if sidebar is expanded
                this.$data._preventCloseOnClickOutside = true;
                this.open();
            }
        }
        eventBus.$on('sidenav-selected', this.handleTooltipOpened);
    },
    methods: {
        triggerWhatsNewEvent() {
            const key = 'showWhatsNew';
            const value = 'true';

            localStorage.setItem(key, value);

            const event = new StorageEvent('storage', {
                key,
                newValue: value,
            });
            window.dispatchEvent(event);
        },
        toggleExpand() {
            if(this.route.name === 'whats-new-modal-trigger') {
                this.triggerWhatsNewEvent();
                return
            }
            this.$emit('toggled');
            if (!this.children) {
                return;
            }
            if (this.expanded) {
                this.close();
            } else {
                this.open();
            }
        },
        open() {
            const cmp = this;
            if (this.expanded) {
                return;
            }
            this.expanded = true;
            document.addEventListener('click', this.handleClickOutside);
            eventBus.$emit('sidenav-selected', {uuid: this.uuid});
            eventBus.$on('sidenav-selected', this.handleTooltipOpened);

            // check to see if the expanded categories will overflow the screen, and set them to expand upwards
            if (this.nav_parent.sidebarCollapsed) {
                cmp.$nextTick(() => {
                    const children = cmp.$refs.children;
                    const rect = children.getBoundingClientRect();
                    const window_height = getWindowDimensions().height;

                    if (rect.top + rect.height > window_height) {
                        cmp.$el.classList.add('expand-top');
                        // calculate offset
                        cmp.$refs.children.style.bottom = (window_height - rect.top) - 36 + 'px';
                    }
                });
            }
        },
        close() {
            if (!this.expanded) {
                return;
            }
            this.expanded = false;
            document.removeEventListener('click', this.handleClickOutside);
            eventBus.$off('sidenav-selected', this.handleTooltipOpened);

            // avoid undefined
            if (!this.$el.classList) return;
            // cleanup upwards-expanding classes
            if (this.$el.classList.contains('expand-top')) {
                this.$el.classList.remove('expand-top');
                this.$refs.children.style.bottom = 'unset';
            }
        },
        handleClickOutside(event) {
            // if the click targeted the collapse sidenav button we should close the category
            if (this.nav_parent.$refs.collapseButton.contains(event.target)) {
                this.close();
                return;
            }
            if (this.$data._preventCloseOnClickOutside) {
                this.$data._preventCloseOnClickOutside = false;
                return;
            }
            if (this.expanded && this.nav_parent.sidebarCollapsed && !this.$el.contains(event.target)) {
                this.close();
            }
        },
        handleTooltipOpened({uuid}) {
            if (uuid !== this.uuid) {
                this.close();
            }
        },
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside);
    },
    computed: {
        active() {
            if (this.route.noHighlight) {
                return false;
            }
            if (this.parent_active !== undefined && this.parent_active === this.route.name) {
                return true;
            }
            return this.route.active || this.expanded;
        },
        children() {
            if (!this.route.children || this.route.children.length < 1) {
                return undefined;
            }

            let children = this.deepCloneChildren ? cloneDeep(this.route.children) : this.route.children;

            if (this.category_link_as_child) {
                // include category title link as first child
                const category = cloneDeep(this.route);
                delete category.children;
                delete category.icon;
                category.noHighlight = true;
                children = [category, ...children];
            }
            // filter out hidden children
            children = children.filter((child) => child.hide_navigation !== true);
            if (children.length > 0) {
                return children;
            }
            return undefined;
        },
    },
    template: `
        <div v-if="!route.hide_navigation"
             :class="['nav-category', expanded ? 'expanded' : '']"
        >
            <component
                :is="(children || use_div) ? 'div' : 'apck-app-link'"
                :tabindex="(children || use_div) ? 0 : ''"
                v-bind="!children ? {route: route} : {}"
                :class="['category sidenav-btn', active ? 'active' : '']"
                @click="toggleExpand"
                @keyup.enter="toggleExpand"
                @keyup.space="toggleExpand"
            >
                <slot v-if="icon" name="icon">
                    <span v-bind:class="['nav-icon', 'fa fa-regular', 'fa-lg', route.icon]"></span>
                </slot>

                <span class="nav-name">
                    {{route.title}}
                    <span v-if="route.count" class="category-count">
                        {{ route.count }}
                    </span>
                </span>

                <slot name="dropdown">
                    <i v-if="children && !expanded" class="category-expand fa fa-regular fa-lg fa-chevron-down"></i>
                    <i v-if="children && expanded" class="category-expand fa fa-regular fa-lg fa-chevron-up"></i>
                </slot>
            </component>

            <div v-if="children" v-show="expanded" class="child-categories" ref="children">
                <apck-nav-item
                    v-for="child in children"
                    :nav_parent="nav_parent"
                    :route="child"
                    :parent_active="parent_active"
                    :icon="child_icons"
                    :key="child.id"
                />
            </div>

        </div>
    `
}

const ApckNavNotifications = {
    components: {
        'apck-app-link': ApckAppLink,
        'apck-nav-item': ApckNavItem,
    },
    props: {
        notifications: {
            type: Object,
            default: () => ({})
        },
        nav_parent: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            expanded: false,
            items: [],
        }
    },
    created() {
        this.items = this.notifications.items;
        this.uuid = createUUIDv4();
    },
    methods: {
        toggleExpand() {
            if (this.expanded) {
                this.close();
            } else {
                this.open();
            }
        },
        open() {
            const cmp = this;
            if (this.expanded) {
                return;
            }
            this.expanded = true;
            document.addEventListener('click', this.handleClickOutside);
            eventBus.$emit('sidenav-selected', {uuid: this.uuid});
            eventBus.$on('sidenav-selected', this.handleTooltipOpened);

            AjaxUtils.ajax_get({
                url: '/notices/seen/all/',
                success: function (data) {
                    cmp.notifications.count = 0;
                },
            });
        },
        close() {
            if (!this.expanded) {
                return;
            }
            this.expanded = false;
            document.removeEventListener('click', this.handleClickOutside);
            eventBus.$off('sidenav-selected', this.handleTooltipOpened);
        },
        dismissItem(item, index) {
            const cmp = this;
            AjaxUtils.ajax_get({
                url: `/notices/dismiss/${item._id}`,
                success: function (data) {
                    cmp.items.splice(index, 1);
                },
            });
        },
        clearAll() {
            const cmp = this;
            AjaxUtils.ajax_get({
                url: '/notices/dismiss/all/',
                success: function (data) {
                    cmp.items = [];
                },
            });
        },
        handleClickOutside(event) {
            // if the click targeted the collapse sidenav button we should close the category
            if (this.nav_parent.$refs.collapseButton.contains(event.target)) {
                this.close();
                return;
            }
            if (this.expanded && !this.$el.contains(event.target)) {
                this.close();
            }
        },
        handleTooltipOpened({uuid}) {
            if (uuid !== this.uuid) {
                this.close();
            }
        },
    },
    template: `
        <div :class="expanded ? 'notifications-expanded' : ''">
            <apck-nav-item
                @toggled="toggleExpand"
                :nav_parent="nav_parent"
                :use_div="true"
                :route="{
                    title: notifications.title,
                    name: notifications.name,
                    icon: notifications.icon,
                    count: notifications.count,
                }"
            />
            <div v-if="expanded" class="child-categories notification-children">
                <template v-if="items.length > 0">
                    <div v-for="(item, index) in items" class="nav-category" :key="item._id">
                        <apck-app-link
                            class="category sidenav-btn"
                            tabindex="0"
                            :route="{
                                name: item.name,
                                path: item.path,
                                icon: item.icon,
                                meta: item.meta,
                            }"
                        >
                            <span v-bind:class="['nav-icon', 'fa-regular', 'fa-lg', item.icon]"></span>
                            <span class="nav-name">
                                {{ item.title }}
                                <span class="nav-notification-icons">
                                    <span v-if="!item.seen" class="category-count fa fa-standard fa-lg fa-exclamation"></span>
                                    <span tabindex="0"
                                          @click.prevent="dismissItem(item, index)"
                                          @keyup.enter.prevent="dismissItem(item, index)"
                                          class="category-count icon-dismiss fa fa-user-xmark"
                                    ></span>
                                </span>
                            </span>
                        </apck-app-link>
                    </div>

                    <div class="notification-actions">
                        <div class="sidenav-btn" tabindex="0" @click="clearAll" @keyup.enter="clearAll">
                            Clear All
                        </div>
                    </div>
                </template>

                <div v-else class="nav-category">
                    <div class="category">No notifications</div>
                </div>
            </div>
        </div>
    `
}

export const ApckNavigation = {
    components: {
        'apck-nav-item': ApckNavItem,
        'apck-nav-notifications': ApckNavNotifications
    },
    props: {
        links: {
            type: Array,
            default: null
        },
        extraLinks: {
            type: Array,
            default: null
        },
        support: {
            type: Boolean,
            default: false
        },
        hideLogo: {
            type: Boolean,
            default: false
        },
        startCollapsed: {
            type: Boolean,
            default: false
        },
        notifications: {
            type: Object,
            default: null
        },
        user: {
            type: Object,
            default: null
        },
        impersonation: {
            type: Object,
            default: null
        },
        userSettingsUrl: {
            type: String,
            default: '/settings'
        },
        logoutUrl: {
            type: String,
            default: '/security/logout'
        },
        routeFilter: {
            type: Function,
            default: undefined,
        },
        // flag for adding the "What's new" section
        // true by default
        whatsNew: {
            type: Boolean,
            default: true,
        },
        // flag for adding the "Trash" section
        // true by default
        trash: {
            type: Boolean,
            default: true,
        },
        // flag for DISABLING since we want to have this by default.
        // this way it's easy to set this in a template
        adminPanelDisabled: {
            type: Boolean,
            default: false,
        },
        rightArrowClass: {
            type: String,
            default: 'fa-arrow-right-to-line',
        },
        leftArrowClass: {
            type: String,
            default: 'fa-arrow-left-to-line',
        },
        // this has a MASSIVE impact on performance and every single
        // navigation that happens. I'll hide this behind a flag but I
        // believe this should be the new default
        deepCloneChildren: {
            type: Boolean,
            default: true,
        },
        getParent: {
            type: Function,
            default: undefined,
        },
    },
    data: function() {
        const cmp = this;
        const get_nav_tree = function () {
            if (cmp.links) {
                return cmp.links;
            }

            const scanner_route_filter = (route) => route.meta?.scanner_nav_only || false;
            const admin_route_filter = (route) => route.meta?.admin_nav_only || false;
            const not_superadmin_route_filter = (route) => !route.meta?.appcheck_admin_only;
            const scan_hubs_admin_route_filter = (route) => !route.meta?.scan_hubs_admin_only;

            let filter;
            if (!cmp.routeFilter) {
                if (cmp.$route.meta?.admin_nav_only && cmp.$permissions?.backend_permission) {
                    filter = admin_route_filter;
                } else {
                    filter = scanner_route_filter;
                }
                if (!cmp.$permissions?.superadmin_group) {
                    const _filter = filter;
                    filter = (route) => _filter(route) && not_superadmin_route_filter(route);
                }
                if (!cmp.$permissions?.can_change_scan_hubs) {
                    const _filter = filter;
                    filter = (route) => _filter(route) && scan_hubs_admin_route_filter(route);
                }
            } else {
                filter = cmp.routeFilter;
            }
            return filter_routes(cmp.$router.options.tree, filter);
        }

        const get_extra_routes = function () {
            if (cmp.extraLinks) {
                return cmp.extraLinks;
            }

            const links = [];

            if (cmp.$route.meta?.admin_nav_only && cmp.$permissions?.backend_permission) {
                links.push({
                    title: 'Back to Scanner',
                    name: 'dashboard',
                    path: cmp.$router.resolve({name: 'dashboard'}).route.path,
                    icon: 'fa-undo',
                })
            } else if (!cmp.adminPanelDisabled) {
                links.push({
                    title: 'Admin Panel',
                    name: 'admin',
                    path: cmp.$router.resolve({name: 'admin'}).route.path,
                    icon: 'fa-briefcase',
                })
            }

            if (cmp.support) {
                links.push({
                    title: 'Support',
                    path: 'https://support.appcheck-ng.com/hc/en-us',
                    icon: 'fa-headset',
                    meta: {
                        isExternal: true,
                        newTab: true
                    }
                });
            }

            if (cmp.trash) {
                links.push({
                    title: 'Trash',
                    name: 'trash',
                    icon: 'fa-trash-can',
                    path: cmp.$router.resolve({name: 'trash'}).route.path,
                    children: cmp.$router.resolve({name: 'trash'}).route.children || []
                });
            }

            if (cmp.whatsNew) {
                links.push({
                    title: 'What\'s New?',
                    name: 'whats-new-modal-trigger',
                    icon: 'fa-sparkles',
                    path: '',
                    use_div: true
                });
            }

            return links;
        }

        return {
            routes: get_nav_tree(),
            extraRoutes: get_extra_routes(),
            sidebarCollapsed: false
        };
    },
    created() {
        this.sidebarCollapsed = this.startCollapsed;
    },
    computed: {
        parent_active: function() {
            if (this.getParent) {
                return this.getParent();
            }
            var name = this.$route.name;
            var parent = name;
            this.$router.options.routes.forEach(function(route){
                if(name == route.name) {
                    if(route.parent) {
                        parent = route.parent;
                    }
                    return;
                }
            });
            return parent;
        },
        cmp() {
            return this;
        }
    },
    methods: {
        toggleCollapse: function() {
            this.sidebarCollapsed = !this.sidebarCollapsed;
            this.$emit('update:collapse', this.sidebarCollapsed);
        },
        openSettings() {
            window.location.href = this.userSettingsUrl;
        },
        impersonationBack() {
            if (!this.impersonation.useAjax) {
                window.location.href = this.impersonation.backUrl;
                return;
            }

            AjaxUtils.ajax_get({
                url: this.impersonation.backUrl,
                success: function () {
                    // TODO: Won't need to reload the page when are using the JWT as a datasource instead of session
                    window.location.reload();
                }
            })
        },
        impersonationStop() {
            if (this.impersonation?.depth > 1) {
                return this.impersonationBack();
            }

            if (!this.impersonation.useAjax) {
                window.location.href = this.impersonation.clearUrl;
                return;
            }

            AjaxUtils.ajax_get({
                url: this.impersonation.clearUrl,
                success: function () {
                    // TODO: Won't need to reload the page when are using the JWT as a datasource instead of session
                    window.location.reload();
                }
            })
        },
        logout() {
            window.location.href = this.logoutUrl;
            ClearToken();
        },
        toggleSupportUi() {
            if (!this.$permissions?.superadmin_group) return;
            window.dispatchEvent(
                new CustomEvent('support-ui-toggle')
            );
        }
    },
    template: `
        <div :class="['apck-navigation', sidebarCollapsed ? 'collapsed' : '']">
            <div class="sidenav-header">
                <a href="/" v-if="hideLogo" class="sidenav-logo-full">Home</a>
                <template v-else>
                    <a v-show="!sidebarCollapsed" href="/" class="sidenav-logo-full">
                        <svg xmlns="http://www.w3.org/2000/svg" width="121" height="20" viewBox="0 0 121 20" fill="none">
                            <path d="M35.7608 1.8035H37.8008L43.5194 15.0921H40.8213L39.5841 12.0534H33.8325L32.6348 15.0921H29.9894L35.7608 1.8035ZM38.7617 10.023L36.7217 4.61806L34.6422 10.023H38.7617Z" fill="white"/>
                            <path d="M44.2303 6.08119H46.3689V7.43261H46.4085C46.711 6.90524 47.1652 6.48995 47.718 6.2328C48.2773 5.98253 48.8892 5.85051 49.5012 5.85739C50.1396 5.84398 50.7715 5.96912 51.3569 6.21974C51.8901 6.45042 52.3704 6.79317 52.7654 7.22153C53.1601 7.65676 53.4564 8.17071 53.6472 8.72455C53.8511 9.32446 53.9497 9.95049 53.9432 10.5834C53.9497 11.2163 53.838 11.8489 53.6207 12.4422C53.4234 12.9957 53.1209 13.5035 52.7324 13.9449C52.3508 14.3736 51.8771 14.7164 51.3569 14.947C50.8045 15.1973 50.1987 15.3163 49.5933 15.3094C49.2117 15.3163 48.8366 15.2699 48.4681 15.1777C48.1656 15.0987 47.8758 14.9866 47.6062 14.8415C47.3759 14.7164 47.1652 14.5713 46.9678 14.3997C46.8097 14.2615 46.6649 14.1034 46.5333 13.9384H46.4741V19.3633H44.2234V6.08119H44.2303ZM51.6928 10.5899C51.6928 10.2472 51.6333 9.90442 51.5151 9.58161C51.3965 9.25845 51.2191 8.96829 50.9887 8.71802C49.9883 7.64369 48.3038 7.59075 47.2308 8.59288C47.1916 8.63242 47.1456 8.67195 47.106 8.71802C46.8757 8.96829 46.6979 9.25845 46.5793 9.58161C46.3425 10.2341 46.3425 10.9526 46.5793 11.6117C46.6979 11.9348 46.8757 12.2246 47.106 12.4752C48.1061 13.5496 49.791 13.6022 50.8636 12.6004C50.9031 12.5608 50.9492 12.5213 50.9887 12.4752C51.2191 12.2246 51.3965 11.9348 51.5151 11.6117C51.6333 11.2754 51.6928 10.9327 51.6928 10.5899Z" fill="white"/>
                            <path d="M55.4764 6.08119H57.6155V7.43261H57.6547C57.9575 6.90524 58.4117 6.48342 58.9645 6.2328C59.5238 5.98253 60.1292 5.85051 60.7477 5.85739C61.3861 5.84398 62.018 5.96912 62.6034 6.21974C63.1366 6.45042 63.6169 6.79317 64.0119 7.2284C64.4066 7.66329 64.7029 8.17759 64.8937 8.73108C65.0976 9.33099 65.1963 9.95702 65.1963 10.5899C65.2028 11.2228 65.0911 11.8554 64.8803 12.4488C64.683 13.0026 64.3805 13.51 63.992 13.9518C63.6104 14.3801 63.1366 14.7229 62.6168 14.9536C62.064 15.1973 61.4652 15.3228 60.8598 15.3163C60.4778 15.3228 60.1027 15.2764 59.7342 15.1843C59.4317 15.1052 59.1422 14.9931 58.8723 14.848C58.642 14.7229 58.4313 14.5778 58.2339 14.4066C58.0761 14.2681 57.9311 14.1165 57.8063 13.9449H57.7468V19.3698H55.4964V6.08119H55.4764ZM62.9324 10.5899C62.9324 10.2472 62.8733 9.90442 62.755 9.58161C62.6364 9.25845 62.4587 8.96829 62.2284 8.71802C61.2283 7.64369 59.5434 7.59075 58.4708 8.59288C58.4313 8.63242 58.3852 8.67195 58.3457 8.71802C58.1153 8.96829 57.9379 9.25845 57.8193 9.58161C57.5825 10.2341 57.5825 10.9526 57.8193 11.6117C57.9379 11.9348 58.1153 12.2246 58.3457 12.4752C59.3461 13.5496 61.0306 13.6022 62.1036 12.6004C62.1428 12.5608 62.1888 12.5213 62.2284 12.4752C62.4587 12.2246 62.6364 11.9348 62.755 11.6117C62.8733 11.2754 62.9324 10.9327 62.9324 10.5899Z" fill="white"/>
                            <path d="M76.0283 4.92127C75.5415 4.39391 75.0674 4.04462 74.6136 3.87308C74.1726 3.70187 73.7054 3.60939 73.2382 3.60939C72.5998 3.60286 71.9614 3.72147 71.3759 3.97209C70.8361 4.2093 70.3493 4.55205 69.9478 4.98041C69.5463 5.42217 69.2303 5.92959 69.0199 6.48996C68.7961 7.08333 68.6844 7.7159 68.6909 8.3488C68.6844 9.02125 68.7961 9.68681 69.0199 10.3197C69.2238 10.8932 69.5397 11.427 69.9478 11.8753C70.3493 12.3171 70.8361 12.6664 71.3759 12.9101C71.9614 13.1673 72.5998 13.299 73.2382 13.2859C73.7844 13.2859 74.3173 13.1542 74.8044 12.9036C75.3112 12.6465 75.7784 12.2377 76.2191 11.6711L78.167 13.0617C77.6142 13.8528 76.8575 14.4723 75.9757 14.8615C75.107 15.2369 74.166 15.428 73.2182 15.4215C72.2577 15.4349 71.2968 15.2633 70.3954 14.9271C68.7236 14.3011 67.3945 12.9957 66.7296 11.3349C66.368 10.4318 66.1902 9.46954 66.2033 8.50041C66.1902 7.51169 66.368 6.52297 66.7296 5.60025C67.0521 4.76967 67.5458 4.01162 68.1777 3.37871C68.8092 2.75921 69.5593 2.27792 70.3885 1.96163C71.2903 1.61888 72.2443 1.45421 73.2117 1.46727C74.0674 1.46074 74.9227 1.61888 75.7124 1.94857C76.4825 2.26485 77.1931 2.81181 77.8579 3.56986L76.0283 4.92127Z" fill="white"/>
                            <path d="M81.2403 7.34047H81.2799C81.4572 6.96472 81.7601 6.62196 82.2077 6.31875C82.655 6.01553 83.2209 5.85739 83.9253 5.85739C84.5241 5.85739 85.0309 5.95605 85.452 6.1606C85.8467 6.34487 86.1956 6.61509 86.472 6.95131C86.735 7.281 86.9258 7.66982 87.0314 8.07858C87.1431 8.51346 87.2022 8.96176 87.2022 9.41005V15.0987H84.9518V10.5373C84.9518 10.2867 84.9388 10.01 84.9123 9.70021C84.8927 9.40352 84.8201 9.11337 84.7149 8.83662C84.6094 8.57294 84.4385 8.34226 84.2213 8.16418C83.9975 7.97957 83.6884 7.89397 83.3065 7.89397C82.984 7.8809 82.655 7.94691 82.359 8.08511C82.1087 8.20372 81.8918 8.37526 81.7206 8.59288C81.5494 8.81015 81.4246 9.06077 81.3586 9.32445C81.2799 9.60773 81.2469 9.89788 81.2469 10.188V15.1052H78.9961V0.90036H81.2469L81.2403 7.34047Z" fill="white"/>
                            <path d="M90.9863 11.3744C91.0588 12.0465 91.3218 12.5739 91.7759 12.9497C92.2366 13.3254 92.8159 13.53 93.4079 13.51C93.9672 13.51 94.4413 13.3914 94.8229 13.1607C95.2045 12.9301 95.54 12.6334 95.8233 12.2906L97.4353 13.51C96.909 14.1625 96.3232 14.6239 95.6717 14.901C95.0268 15.1777 94.3292 15.3163 93.6317 15.3163C92.9802 15.3228 92.3287 15.2042 91.7233 14.98C91.1441 14.769 90.6177 14.4462 90.1701 14.0243C89.7229 13.6022 89.3674 13.0947 89.124 12.5344C88.8606 11.9214 88.729 11.2624 88.7424 10.5899C88.7355 9.92436 88.8672 9.25845 89.124 8.64548C89.3674 8.08511 89.7229 7.57768 90.1636 7.15586C90.6112 6.73404 91.1375 6.41088 91.7168 6.20014C92.3287 5.97599 92.9737 5.85739 93.6252 5.86392C94.211 5.85739 94.7964 5.96912 95.3427 6.19326C95.8494 6.40435 96.3036 6.72716 96.6721 7.14245C97.0602 7.59075 97.3497 8.11158 97.534 8.67195C97.7513 9.35058 97.8565 10.056 97.8434 10.768V11.3875L90.9863 11.3744ZM95.593 9.6868C95.5796 9.02123 95.3757 8.5004 94.9742 8.11811C94.573 7.73582 94.0202 7.54468 93.3092 7.54468C92.6382 7.54468 92.1049 7.73582 91.7103 8.12464C91.3153 8.51346 91.0784 9.0343 90.9863 9.68027L95.593 9.6868Z" fill="white"/>
                            <path d="M105.523 8.78404C105.28 8.52689 105.003 8.30274 104.701 8.12466C104.431 7.96652 104.128 7.88091 103.819 7.88091C103.082 7.86751 102.378 8.16419 101.877 8.70462C101.647 8.95524 101.469 9.24539 101.351 9.56821C101.114 10.2207 101.114 10.9392 101.351 11.5986C101.469 11.9214 101.647 12.2116 101.877 12.4618C102.378 13.0026 103.082 13.3058 103.819 13.2859C104.154 13.2859 104.49 13.2134 104.793 13.0683C105.096 12.9235 105.359 12.7059 105.563 12.4422L107.063 14.057C106.833 14.2877 106.57 14.4857 106.286 14.6373C106.004 14.7955 105.708 14.9271 105.398 15.0327C105.115 15.1248 104.826 15.1973 104.536 15.2503C104.299 15.2898 104.062 15.3094 103.825 15.3163C103.174 15.3228 102.522 15.2042 101.917 14.9801C101.338 14.769 100.811 14.4462 100.364 14.0243C99.923 13.6022 99.5675 13.0947 99.3241 12.5278C99.0608 11.9149 98.9295 11.2558 98.9425 10.5834C98.936 9.91749 99.0677 9.25193 99.3241 8.63896C99.5675 8.07859 99.923 7.57116 100.364 7.14934C100.811 6.72717 101.345 6.40436 101.917 6.19328C102.529 5.96913 103.174 5.85052 103.825 5.8574C104.418 5.86393 105.003 5.96913 105.556 6.18675C106.142 6.41089 106.662 6.76671 107.083 7.22841L105.523 8.78404Z" fill="white"/>
                            <path d="M107.846 0.90036H110.097V9.81882L113.565 6.08119H116.447L112.591 10.1681L116.697 15.0852H113.716L110.143 10.4913H110.103V15.0921H107.853V0.90036H107.846Z" fill="white"/>
                            <mask id="mask0_21_417" style="mask-type:luminance" maskUnits="userSpaceOnUse" x="0" y="1" width="11" height="19">
                                <path d="M0.165405 1.84818H10.6491V19.89H0.165405V1.84818Z" fill="white"/>
                            </mask>
                            <g mask="url(#mask0_21_417)">
                                <path d="M8.1677 3.99855C7.82563 3.36564 7.7531 2.62066 7.96384 1.92862L0.4879 15.8897C-0.21617 17.2078 0.277504 18.8559 1.59351 19.561C2.90986 20.2664 4.55487 19.7721 5.25894 18.4536L10.6223 8.43439C10.4841 8.27625 10.3655 8.11158 10.2668 7.92697L8.1677 3.99855Z" fill="#00BBD2"/>
                            </g>
                            <path d="M13.939 9.0343C15.255 8.3292 15.7487 6.68109 15.0446 5.36303L13.202 1.92862C13.4123 2.62066 13.3402 3.36564 12.9977 3.99855L10.6222 8.43439C11.4514 9.37705 12.8269 9.62767 13.939 9.0343Z" fill="#00BBD2"/>
                            <path d="M13.202 1.92863L12.9387 1.43427C12.2607 0.135796 10.6552 -0.37163 9.3523 0.307C8.87169 0.557619 8.47703 0.952971 8.22675 1.43427L7.96376 1.92863C7.75302 2.62067 7.82556 3.36565 8.16762 3.99856L10.2736 7.92698C10.3723 8.11159 10.4906 8.2828 10.6288 8.43441L13.0043 3.99856C13.3402 3.36565 13.4124 2.62067 13.202 1.92863Z" fill="#2585A4"/>
                            <path d="M27.3109 0.326942C25.9949 -0.378503 24.3496 0.115859 23.6455 1.43427L18.2821 11.4535C18.4203 11.6117 18.5389 11.7763 18.6376 11.9609L20.7436 15.8897C21.0857 16.5223 21.1579 17.2738 20.9475 17.9593L28.4165 3.99856C29.1206 2.68015 28.6273 1.03239 27.3109 0.326942Z" fill="#4CBEA0"/>
                            <path d="M15.9066 15.8897L18.2821 11.4535C17.4533 10.5043 16.0778 10.2603 14.9656 10.8536C13.6493 11.5591 13.156 13.2068 13.86 14.5252L15.7027 17.9593C15.492 17.2738 15.5645 16.5288 15.9066 15.8897Z" fill="#4CBEA0"/>
                            <path d="M20.7367 15.8897L18.6311 11.9609C18.5324 11.7763 18.4138 11.6051 18.2756 11.4535L15.9 15.8897C15.558 16.5223 15.4854 17.2738 15.6962 17.9593L15.9592 18.4537C16.6371 19.7587 18.2429 20.2599 19.5459 19.5809C20.0261 19.3303 20.4212 18.935 20.6711 18.4537L20.9344 17.9593C21.1448 17.2738 21.0726 16.5288 20.7367 15.8897Z" fill="#009D6F"/>
                        </svg>
                    </a>
                    <a v-show="sidebarCollapsed" href="/" class="sidenav-logo">
                        <svg xmlns="http://www.w3.org/2000/svg" width="30" height="20" viewBox="0 0 30 20" fill="none">
                            <mask id="mask0_28_262" style="mask-type:luminance" maskUnits="userSpaceOnUse" x="0" y="1" width="12" height="20">
                                <path d="M0.630859 1.85342H11.1755V20.0002H0.630859V1.85342Z" fill="white"/>
                            </mask>
                            <g mask="url(#mask0_28_262)">
                                <path d="M8.67957 4.01611C8.33551 3.37952 8.26255 2.63021 8.47452 1.93414L0.955097 15.9764C0.246931 17.3022 0.743477 18.9599 2.06714 19.6691C3.39115 20.3786 5.04572 19.8814 5.75389 18.5553L11.1485 8.47776C11.0095 8.3187 10.8902 8.15307 10.7909 7.96738L8.67957 4.01611Z" fill="#00BBD2"/>
                            </g>
                            <path d="M14.4848 9.08113C15.8085 8.37192 16.305 6.71423 15.5968 5.3885L13.7434 1.93411C13.9551 2.63018 13.8825 3.37949 13.538 4.01608L11.1487 8.47773C11.9827 9.42587 13.3662 9.67795 14.4848 9.08113Z" fill="#00BBD2"/>
                            <path d="M13.7433 1.93411L13.4784 1.43687C12.7965 0.130845 11.1817 -0.379532 9.8712 0.303046C9.38779 0.555122 8.99083 0.952774 8.7391 1.43687L8.47458 1.93411C8.26261 2.63017 8.33557 3.37949 8.67963 4.01608L10.7979 7.96735C10.8971 8.15304 11.0161 8.32524 11.1551 8.47773L13.5445 4.01608C13.8823 3.37949 13.9549 2.63017 13.7433 1.93411Z" fill="#2585A4"/>
                            <path d="M27.9345 0.322983C26.6108 -0.386566 24.9559 0.110672 24.2477 1.43675L18.8531 11.5143C18.9922 11.6733 19.1115 11.839 19.2107 12.0247L21.329 15.9763C21.673 16.6125 21.7456 17.3684 21.534 18.0579L29.0465 4.01596C29.7547 2.68988 29.2585 1.03253 27.9345 0.322983Z" fill="#4CBEA0"/>
                            <path d="M16.4636 15.9762L18.8529 11.5142C18.0193 10.5595 16.6358 10.3139 15.5172 10.9108C14.1932 11.6203 13.697 13.2777 14.4051 14.6037L16.2585 18.0578C16.0466 17.3683 16.1195 16.619 16.4636 15.9762Z" fill="#4CBEA0"/>
                            <path d="M21.3218 15.9766L19.2039 12.025C19.1046 11.8393 18.9853 11.6671 18.8463 11.5146L16.457 15.9766C16.1129 16.6129 16.04 17.3687 16.2519 18.0582L16.5164 18.5555C17.1983 19.8681 18.8135 20.3722 20.124 19.6893C20.6071 19.4372 21.0044 19.0396 21.2558 18.5555L21.5206 18.0582C21.7323 17.3687 21.6596 16.6194 21.3218 15.9766Z" fill="#009D6F"/>
                        </svg>
                    </a>
                </template>

                <div
                    ref="collapseButton"
                    class="sidenav-btn sidebar-toggle"
                    @click="toggleCollapse"
                    @keyup.enter="toggleCollapse"
                    @keyup.space="toggleCollapse"
                    tabindex="0"
                    :title="[sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar']"
                >
                    <i v-if="sidebarCollapsed" :class="[ 'fa fa-regular fa-lg', rightArrowClass ]"></i>
                    <i v-else :class="['fa fa-regular', leftArrowClass]"></i>
                </div>

            </div>

            <div class="navigation nav-middle">
                <apck-nav-item
                    v-for="route in routes"
                    :nav_parent="cmp"
                    :route="route"
                    :parent_active="parent_active"
                    :key="route.id"
                />
            </div>

            <div class="navigation nav-bottom">
                <apck-nav-item
                    v-for="route in extraRoutes"
                    :nav_parent="cmp"
                    :route="route"
                    :key="route.id"
                    :parent_active="parent_active"
                    :use_div="route.use_div"
                />
                <apck-nav-notifications
                    v-if="notifications"
                    :nav_parent="cmp"
                    :notifications="notifications"
                />
            </div>

            <!-- Add slot for custom user menu -->
            <slot name="user-menu">
                <div class="navigation nav-user" v-if="user">
                    <div class="nav-impersonation" v-if="impersonation">
                        <div>
                            <div class="impersonation-text">
                                <span>
                                    <i v-on:click="toggleSupportUi" class="nav-icon fa-regular fa-lg fa-info-circle"></i>
                                    Impersonation mode
                                </span>
                                <p>
                                    You are acting as
                                    <a :href="impersonation.userUrl">
                                        <strong><em>{{ impersonation.name }}</em></strong>
                                    </a> for
                                    <a :href="impersonation.orgUrl">
                                        <strong><em>{{ impersonation.orgName }}</em></strong>
                                    </a>.
                                </p>
                            </div>

                            <div
                                class="sidenav-btn"
                                tabindex="0"
                                @click="impersonationStop"
                                @keyup.enter="impersonationStop"
                            >
                                <div class="impersonation-text imp-text-collapsed">
                                    <p>
                                        You are acting as
                                        <a :href="impersonation.userUrl">
                                            <strong><em>{{ impersonation.name }}</em></strong>
                                        </a> for
                                        <a :href="impersonation.orgUrl">
                                            <strong><em>{{ impersonation.orgName }}</em></strong>
                                        </a>.

                                    </p>
                                </div>
                                <div class="imp-btn-text">
                                    <i class="nav-icon fa fa-lg fa-user-times"></i>
                                    <span class="nav-name">
                                        {{ (impersonation?.depth > 1) ? 'Rollback Impersonation' : 'Stop Impersonating' }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <apck-nav-item
                        :nav_parent="cmp"
                        :child_icons="true"
                        :route="{
                            title: user.name,
                            noHighlight: true,
                            children: [
                                {
                                    title: 'Settings',
                                    route: 'settings',
                                    icon: 'fa-cog',
                                    handler: openSettings,
                                    noHighlight: true
                                },{
                                    title: 'Activity',
                                    route: 'activity',
                                    icon: 'fa-history',
                                    path: user.activityUrl,
                                    meta: {isExternal: true}
                                },{
                                    title: 'Log out',
                                    route: 'logout',
                                    icon: 'fa-sign-out',
                                    handler: logout,
                                    noHighlight: true,
                                },
                            ]
                        }"
                        :use_div="true"
                        class="user-button"
                    >
                        <template v-slot:icon>
                            <div class="user-icon">
                                {{ user.name.split(' ').slice(0, 2).map(word => word[0]?.toUpperCase()).join('') }}
                            </div>
                        </template>
                        <template v-slot:dropdown>
                            <i class="fa-solid fa-ellipsis-v category-expand"></i>
                        </template>
                    </apck-nav-item>
                </div>
            </slot>

            <div v-if="impersonation" id="impersonation-warning"></div>
        </div>
    `
}
